import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React from "react";

import { yesOrNo } from "../../../Enums/enumValues";
import { YesOrNo } from "../../../interfaces/enum";
import { ModelState } from "../../../interfaces/modelState";
import { DlpState, InsuredPerson } from "../../../providers/dlpFlow/DlpFlow.interfaces";
import { force } from "../../../typeguards/force";
import { Card } from "../../card/Card";
import DateInput from "../../InputFields/DateInput";
import HorizontalRadioInput, { RadioOption } from "../../InputFields/HorizontalRadioInput";
import { V27, V48, V58 } from "../../InputFields/sanitizer/inputSanitizers";
import TextInput from "../../InputFields/TextInput";
import { V5, V6, V10, V11 } from "../../InputFields/validator/validators";

const SHOULD_RETRIEVE_PREMIUM = true;

interface Props {
  fields: Record<string, Field<string>>;
  data: DlpState;
  modelState: ModelState | null;
  setModelState: (updatedModelState: ModelState) => void;
  onUpdateInsured: (index: number, data: Partial<InsuredPerson>, shouldRetrievePremium?: boolean) => void;
  onAddInsuredPerson: () => void;
  onRemoveInsuredPerson: (index: number) => void;
}

export const InsuredPersons = ({
  fields,
  data,
  onUpdateInsured,
  onAddInsuredPerson,
  onRemoveInsuredPerson,
  modelState,
  setModelState,
}: Props) => {
  const handleFirstname = (firstName: string) => {
    onUpdateInsured(0, { firstName });
  };

  const handleDateOfBirth = (dateOfBirth: string) => {
    onUpdateInsured(0, { dateOfBirth }, SHOULD_RETRIEVE_PREMIUM);
  };

  const handleSmoking = (isSmoking: YesOrNo) => {
    onUpdateInsured(0, { isSmoking }, SHOULD_RETRIEVE_PREMIUM);
  };

  const handleFirstnameSecondInsured = (firstName: string) => {
    onUpdateInsured(1, { firstName });
  };

  const handleDateOfBirthSecondInsured = (dateOfBirth: string) => {
    onUpdateInsured(1, { dateOfBirth }, SHOULD_RETRIEVE_PREMIUM);
  };

  const handleSmokingSecondInsured = (isSmoking: YesOrNo) => {
    onUpdateInsured(1, { isSmoking }, SHOULD_RETRIEVE_PREMIUM);
  };

  const toggleSecondPerson = (value: YesOrNo) => {
    if (value === "Yes") {
      onAddInsuredPerson();
    } else {
      onRemoveInsuredPerson(1);
    }
  };

  const isSecondPersonInsuredSelected = () =>
    data.insuredPersons.length === 2 &&
    data.coverages.length !== 0 &&
    data.coverages.some((c) => c.effectiveInsured?.includes(force(data.insuredPersons[1].personId)));

  const insureSecondPersonOptions = yesOrNo() as RadioOption<YesOrNo>[];
  if (insureSecondPersonOptions.length) {
    // No disabled if secondInsured is selected on a coverage
    insureSecondPersonOptions[1].disabled = isSecondPersonInsuredSelected();
  }

  return (
    <Card>
      <h2>
        <Text field={fields.InsuredTitle} />
      </h2>
      <TextInput
        fieldData={fields}
        name="FirstNameFirstInsured"
        fieldName="FirstInsuredFirstName"
        sanitize={[V27, V48, V58]}
        validators={[V5, V10(35), V11(2)]}
        required
        setModelState={setModelState}
        inputValue={data.insuredPersons[0].firstName}
        modelState={modelState}
        propertyName="model.InsuredPersons[0].FirstName"
        onBlur={handleFirstname}
      />
      <DateInput
        fieldData={fields}
        name="DateOfBirthFirstInsured"
        fieldName="FirstInsuredDateOfBirth"
        validators={[V5, V6]}
        required
        setModelState={setModelState}
        inputValue={data.insuredPersons[0].dateOfBirth}
        modelState={modelState}
        propertyName="model.InsuredPersons[0].DateOfBirth"
        onChange={handleDateOfBirth}
      />
      <HorizontalRadioInput
        fieldData={fields}
        name="SmokingFirstInsured"
        fieldName="FirstInsuredIsSmoking"
        required
        validators={[V5]}
        setModelState={setModelState}
        selectedValue={data.insuredPersons[0].isSmoking}
        options={yesOrNo() as RadioOption<YesOrNo>[]}
        modelState={modelState}
        propertyName="model.InsuredPersons[0].IsSmoking"
        onChange={handleSmoking}
      />

      <div className="form-row-divider"></div>
      <HorizontalRadioInput
        fieldData={fields}
        selectedValue={data.hasSecondInsured}
        name="SecondInsured"
        options={insureSecondPersonOptions}
        required
        validators={[V5]}
        setModelState={setModelState}
        modelState={modelState}
        propertyName="model.HasSecondInsured"
        fieldName="HasSecondInsured"
        onChange={toggleSecondPerson}
      />

      {data.insuredPersons.length === 2 && (
        <div>
          <TextInput
            fieldData={fields}
            required
            sanitize={[V27, V48, V58]}
            validators={[V5, V10(35), V11(2)]}
            setModelState={setModelState}
            name="FirstNameSecondInsured"
            fieldName="SecondInsuredFirstName"
            inputValue={data.insuredPersons[1].firstName}
            modelState={modelState}
            propertyName="model.InsuredPersons[1].FirstName"
            onBlur={handleFirstnameSecondInsured}
          />
          <DateInput
            fieldData={fields}
            required
            validators={[V5, V6]}
            setModelState={setModelState}
            name="DateOfBirthSecondInsured"
            fieldName="SecondInsuredDateOfBirth"
            inputValue={data.insuredPersons[1].dateOfBirth}
            modelState={modelState}
            propertyName="model.InsuredPersons[1].DateOfBirth"
            onChange={handleDateOfBirthSecondInsured}
          />
          <HorizontalRadioInput
            fieldData={fields}
            required
            validators={[V5]}
            setModelState={setModelState}
            name="SmokingSecondInsured"
            fieldName="SecondInsuredIsSmoking"
            selectedValue={data.insuredPersons[1].isSmoking}
            options={yesOrNo() as RadioOption<YesOrNo>[]}
            modelState={modelState}
            propertyName="model.InsuredPersons[1].IsSmoking"
            onChange={handleSmokingSecondInsured}
          />
        </div>
      )}
    </Card>
  );
};
